import React, { useState, useEffect } from 'react';
import { Button, Form, Spinner, Container, Row, Col, Alert, Card } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { getOrganizations, initOrganization, createOrganization, updateOrganization } from './../../api';
import useAuth from '../../hooks/useAuth';

const OrganizationSetup = ({ onCreated }) => {
  const auth = useAuth();
  const isAdmin = auth.isAdminAuth();

  const [formData, setFormData] = useState({
    name: '',
    desc: '',
    max_users: 10,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {

      const response = await createOrganization(formData);
      const data = response.data;
      //console.log(data);

      if (response.status !== 200) {
        throw new Error(data.message || 'Failed to create organization');
      }

      onCreated(data.data);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container fluid className="d-flex flex-column mb-5 px-3">
      <Row className="justify-content-center g-5 pt-3">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h4>Initialize Organization</h4>
            </Card.Header>
            <Card.Body>
              {isAdmin && (
                <>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Organization Name*</Form.Label>
                      <Form.Control
                        required
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter organization name"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Description*</Form.Label>
                      <Form.Control
                        required
                        value={formData.desc}
                        onChange={e => setFormData({ ...formData, desc: e.target.value })}
                        placeholder="Enter organization desc"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Max Users</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={formData.max_users}
                        onChange={e => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Start Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={formData.startDate}
                            onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>End Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={formData.endDate}
                            onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Button
                      type="submit"
                      className="w-100"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Creating...
                        </>
                      ) : (
                        'Create Organization'
                      )}
                    </Button>
                  </Form>
                </>
              )}
            </Card.Body>
          </Card>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}
        </Col>
      </Row>
    </Container>
  );
};

const OrganizationManagement = () => {
  const title = "Organization Management";
  const [organization, setOrganization] = useState(null);
  const [hasOrg, setHasOrg] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [alert, setAlert] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const auth = useAuth();
  const isAdmin = auth.isAdminAuth();

  useEffect(() => {
    checkOrganization();
  }, []);

  const checkOrganization = async () => {
    try {
      const response = await initOrganization();
      const data = response.data;
      setHasOrg(data.exists);
      if (data.exists) {
        fetchOrganization();
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setAlert({ type: 'danger', message: 'Failed to check organization status' });
      setIsLoading(false);
    }
  };

  const fetchOrganization = async () => {
    try {
      const response = await getOrganizations();
      const data = response.data;
      setOrganization(data);
    } catch (error) {
      setAlert({ type: 'danger', message: 'Failed to fetch organization' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {

      const response = await updateOrganization(organization.id, organization);
      const data = response.data;

      if (response.status !== 200) {
        throw new Error(data.message || 'Failed to update organization');
      }
      setAlert({ type: 'success', message: 'Organization updated successfully' });
      setIsEditing(false);
    } catch (error) {
      setAlert({ type: 'danger', message: error.message });
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (!hasOrg) {
    return <OrganizationSetup onCreated={(org) => {
      setOrganization(org);
      setHasOrg(true);
    }} />;
  }

  if (!organization) return null;

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container fluid className="d-flex flex-column mb-5 px-3">
        <Row className="justify-content-center g-5 pt-3">
          <Col md={6}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Organization Details</h5>
                {!isEditing && isAdmin && (
                  <Button variant="btn btn-outline-primary" size="sm" onClick={() => setIsEditing(true)}>
                    <i className="bi bi-pencil me-1"></i>Edit
                  </Button>
                )}
              </Card.Header>
              <Card.Body>

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Organization Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={organization.name}
                      onChange={(e) => setOrganization({ ...organization, name: e.target.value })}
                      disabled={!isEditing}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      value={organization.desc}
                      onChange={(e) => setOrganization({ ...organization, desc: e.target.value })}
                      disabled={!isEditing}
                    />
                  </Form.Group>

                  <Card className="mb-3">
                    <Card.Header>Subscription Details</Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Type</Form.Label>
                            <Form.Control
                              value={organization.subscription.type}
                              disabled
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Label>Status</Form.Label>
                          <Form.Select
                            name="status"
                            value={organization.subscription.status}
                            disabled={!isEditing}
                            onChange={(e) => setOrganization({
                              ...organization,
                              subscription: {
                                ...organization.subscription,
                                status: e.target.value
                              }
                            })}
                            style={{ fontSize: '0.8rem' }}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </Form.Select>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Max Users</Form.Label>
                            <Form.Control
                              type="number"
                              value={organization.subscription.max_users}
                              onChange={(e) => setOrganization({
                                ...organization,
                                subscription: {
                                  ...organization.subscription,
                                  max_users: parseInt(e.target.value)
                                }
                              })}
                              disabled={!isEditing}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Current Users</Form.Label>
                            <Form.Control
                              value={organization.subscription.current_users}
                              disabled
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Start Date</Form.Label>
                            <Form.Control
                              type="date"
                              value={organization.subscription.startDate}
                              onChange={(e) => setOrganization({
                                ...organization,
                                subscription: {
                                  ...organization.subscription,
                                  startDate: e.target.value
                                }
                              })}
                              disabled={!isEditing}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                              type="date"
                              value={organization.subscription.endDate}
                              onChange={(e) => setOrganization({
                                ...organization,
                                subscription: {
                                  ...organization.subscription,
                                  endDate: e.target.value
                                }
                              })}
                              disabled={!isEditing}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {isEditing && (
                    <div className="d-flex justify-content-end gap-2">
                      <Button variant="secondary" onClick={() => setIsEditing(false)}>
                        Cancel
                      </Button>
                      <Button variant="primary" type="submit">
                        Save Changes
                      </Button>
                    </div>
                  )}
                </Form>
              </Card.Body>
            </Card>
            {alert && (
              <Alert variant={alert.type} onClose={() => setAlert(null)} dismissible>
                {alert.message}
              </Alert>
            )}
          </Col>


        </Row>
      </Container>
    </>
  );
};

export default OrganizationManagement;